import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Calendar, Check, MapPin, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { TimePeriod } from "./DateFilterPills";
import type { DateFilterType } from "./FiltersPanel";
import { useUnifiedFilterCounts } from "@/hooks/useUnifiedFilterCounts";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRegions } from "@/hooks/useRegions";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/analytics";

type TabId = "regions" | "categories" | "sources";

export interface DialogFilterDraft {
  selectedCategories: string[];
  excludedCategories: string[];
  selectedSources: string[];
  excludedSources: string[];
  selectedRegions: string[];
  excludedRegions: string[];
  dateFilter: DateFilterType;
  customDate: string | null;
  timePeriods: TimePeriod[];
}

interface FiltersDialogProps {
  open: boolean;
  categories: string[];
  sources: string[];
  lockedRegions?: string[];
  initialDraft: DialogFilterDraft;
  onApply: (draft: DialogFilterDraft) => void;
  onCancel: () => void;
}

const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/* ─── Pure draft-logic helpers (exported for unit tests) ─── */

export function emptyFilterDraft(lockedRegions: string[] = []): DialogFilterDraft {
  return {
    selectedCategories: [],
    excludedCategories: [],
    selectedSources: [],
    excludedSources: [],
    selectedRegions: [...lockedRegions],
    excludedRegions: [],
    dateFilter: null,
    customDate: null,
    timePeriods: ["allDay"],
  };
}

const SELECTED_KEY = {
  regions: "selectedRegions",
  categories: "selectedCategories",
  sources: "selectedSources",
} as const;
const EXCLUDED_KEY = {
  regions: "excludedRegions",
  categories: "excludedCategories",
  sources: "excludedSources",
} as const;
const FACET_NOUNS: Record<TabId, { one: string; many: string }> = {
  regions: { one: "region", many: "regions" },
  categories: { one: "category", many: "categories" },
  sources: { one: "source", many: "sources" },
};

export function formatDraftResultCount(count: number | null | undefined, loading: boolean) {
  if (loading || count == null) return "Updating…";
  if (count === 0) return "No events match these filters";
  return `${count.toLocaleString()} ${count === 1 ? "event" : "events"} match these filters`;
}

/** Plain-language summary of a facet's selection. No selection means every option is included. */
export function formatFacetSelectionSummary(facet: TabId, selected: string[]) {
  const { one, many } = FACET_NOUNS[facet];
  if (selected.length === 0) return `All ${many}`;
  if (selected.length === 1) return `Only ${selected[0]}`;
  if (selected.length === 2) return `Only ${selected[0]} and ${selected[1]}`;
  return `${selected.length} ${selected.length === 1 ? one : many} selected`;
}

/**
 * Convert a date filter to the concrete date strings sent to the API.
 * Mirrors Home.tsx `getDateFilterDates` exactly (including Sat/Sun weekend
 * behaviour) so draft counts and applied results always agree.
 *
 * Accepts an optional `today` override to make the function testable without
 * mocking the system clock.
 */
export function derivedDatesForFilter(
  dateFilter: DateFilterType,
  customDate: string | null,
  today: Date = new Date(),
): string[] | undefined {
  if (!dateFilter) return undefined;

  const fmt = (d: Date) => d.toLocaleDateString("en-CA");
  const base = new Date(today);
  base.setHours(0, 0, 0, 0);

  if (dateFilter === "today") return [fmt(base)];

  if (dateFilter === "tomorrow") {
    const t = new Date(base);
    t.setDate(base.getDate() + 1);
    return [fmt(t)];
  }

  if (dateFilter === "custom") return customDate ? [customDate] : undefined;

  // "weekend" — Fri + Sat + Sun of the current/approaching weekend.
  // On Sunday: previous Saturday and today (Sunday). On Saturday: today + tomorrow.
  // Mon–Fri: upcoming Fri/Sat/Sun.
  const dow = base.getDay();
  let sat: Date;
  let sun: Date;

  if (dow === 0) {
    // Sunday — the weekend is yesterday (Sat) + today (Sun)
    sun = new Date(base);
    sat = new Date(base);
    sat.setDate(base.getDate() - 1);
  } else if (dow === 6) {
    // Saturday — the weekend is today (Sat) + tomorrow (Sun)
    sat = new Date(base);
    sun = new Date(base);
    sun.setDate(base.getDate() + 1);
  } else {
    // Mon–Fri — upcoming Saturday
    sat = new Date(base);
    sat.setDate(base.getDate() + (6 - dow));
    sun = new Date(sat);
    sun.setDate(sat.getDate() + 1);
  }

  const fri = new Date(sat);
  fri.setDate(sat.getDate() - 1);

  return [fmt(fri), fmt(sat), fmt(sun)];
}

/**
 * Toggle a single facet value in/out of the selected (include) list. Returns new draft.
 * Selecting narrows results to the chosen values; an empty selection means "all".
 * Any legacy exclusions for the facet are dropped because the selection replaces them.
 */
export function applyFacetToggle(
  draft: DialogFilterDraft,
  facet: TabId,
  value: string,
  lockedRegions: string[] = [],
): DialogFilterDraft {
  if (facet === "regions" && lockedRegions.includes(value)) return draft;
  const selected = draft[SELECTED_KEY[facet]];
  return {
    ...draft,
    [SELECTED_KEY[facet]]: selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
    [EXCLUDED_KEY[facet]]: [],
  };
}

/** Clear a facet's selection so every option is included again. Route-locked regions stay selected. */
export function clearFacetSelection(
  draft: DialogFilterDraft,
  facet: TabId,
  lockedRegions: string[] = [],
): DialogFilterDraft {
  return {
    ...draft,
    [SELECTED_KEY[facet]]: facet === "regions" ? [...lockedRegions] : [],
    [EXCLUDED_KEY[facet]]: [],
  };
}

/**
 * Convert legacy exclusion state (e.g. from an old bookmarked URL with
 * `excludedRegions=…`) into the equivalent selection for one facet.
 * Returns the same draft object when there is nothing to convert, so it is safe
 * to call repeatedly. A state that hides every option is left untouched because
 * no selection can express it.
 */
export function convertExclusionsToSelection(
  draft: DialogFilterDraft,
  facet: TabId,
  allValues: string[],
  lockedRegions: string[] = [],
): DialogFilterDraft {
  const excluded = draft[EXCLUDED_KEY[facet]];
  if (excluded.length === 0 || allValues.length === 0) return draft;
  const locked = facet === "regions" ? lockedRegions : [];
  const remaining = activeValues(allValues, draft[SELECTED_KEY[facet]], excluded, locked);
  if (remaining.length === 0) return draft;
  const coversAll = remaining.length === allValues.length;
  return {
    ...draft,
    [SELECTED_KEY[facet]]: coversAll ? [...locked] : [...new Set([...locked, ...remaining])],
    [EXCLUDED_KEY[facet]]: [],
  };
}

/** Apply convertExclusionsToSelection to every facet. Returns the same draft when nothing changes. */
export function normalizeDraftToSelection(
  draft: DialogFilterDraft,
  facetValues: Record<TabId, string[]>,
  lockedRegions: string[] = [],
): DialogFilterDraft {
  return (["regions", "categories", "sources"] as TabId[]).reduce(
    (next, facet) => convertExclusionsToSelection(next, facet, facetValues[facet], lockedRegions),
    draft,
  );
}

/** Toggle a date filter quick-pick pill. Returns new draft. */
export function applyDateFilter(
  draft: DialogFilterDraft,
  filter: DateFilterType,
  customDate?: string,
): DialogFilterDraft {
  if (filter === "custom") {
    return { ...draft, dateFilter: "custom", customDate: customDate ?? draft.customDate };
  }
  const next = draft.dateFilter === filter ? null : filter;
  return { ...draft, dateFilter: next, customDate: next ? draft.customDate : null };
}

/** Toggle a time period pill. Returns new draft. */
export function applyTimePeriodToggle(draft: DialogFilterDraft, clicked: TimePeriod): DialogFilterDraft {
  if (clicked === "allDay") return { ...draft, timePeriods: ["allDay"] };
  const without = draft.timePeriods.filter((p) => p !== "allDay" && p !== clicked);
  const next = draft.timePeriods.includes(clicked) ? without : [...without, clicked];
  if (next.length === 0) return { ...draft, timePeriods: ["allDay"] };
  const allThree = (["morning", "afternoon", "evening"] as TimePeriod[]).every((p) => next.includes(p));
  return { ...draft, timePeriods: allThree ? ["allDay"] : next };
}

/* ─── Internal helpers ─── */

function activeValues(
  allValues: string[],
  selectedValues: string[],
  excludedValues: string[],
  lockedValues: string[] = [],
) {
  const explicit = selectedValues.length > 0 ? selectedValues : lockedValues;
  return explicit.length > 0
    ? allValues.filter((v) => explicit.includes(v) && !excludedValues.includes(v))
    : allValues.filter((v) => !excludedValues.includes(v));
}

/** "Sat, Oct 3" for a "YYYY-MM-DD" date picked in the dialog. */
export function formatPickedDate(dateString: string): string {
  const [y, m, d] = dateString.split("-").map(Number);
  if (!y || !m || !d) return dateString;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

export type ActiveFilterKey = "date" | "time" | TabId;

/**
 * The visitor's pending choices as short labels, one per kind of filter, in the order the
 * dialog shows them. Route-locked regions are left out: they aren't the visitor's to remove.
 */
export function describeActiveFilters(
  draft: DialogFilterDraft,
  lockedRegions: string[] = [],
): { key: ActiveFilterKey; label: string }[] {
  const chips: { key: ActiveFilterKey; label: string }[] = [];
  const dateLabel =
    draft.dateFilter === "today" ? "Today"
    : draft.dateFilter === "tomorrow" ? "Tomorrow"
    : draft.dateFilter === "weekend" ? "This weekend"
    : draft.dateFilter === "custom" && draft.customDate ? formatPickedDate(draft.customDate)
    : null;
  if (dateLabel) chips.push({ key: "date", label: dateLabel });
  if (!draft.timePeriods.includes("allDay")) {
    const periods = (["morning", "afternoon", "evening"] as TimePeriod[]).filter((p) => draft.timePeriods.includes(p));
    chips.push({ key: "time", label: periods.map((p) => p[0].toUpperCase() + p.slice(1)).join(", ") });
  }
  (["regions", "categories", "sources"] as TabId[]).forEach((facet) => {
    const picks = draft[SELECTED_KEY[facet]].filter((v) => !(facet === "regions" && lockedRegions.includes(v)));
    if (picks.length === 0) return;
    const names = picks.length <= 2 ? picks.join(", ") : `${picks.length} ${FACET_NOUNS[facet].many}`;
    chips.push({ key: facet, label: facet === "sources" && picks.length <= 2 ? `Source: ${names}` : names });
  });
  return chips;
}

/* ─── Component ─── */

const TAB_LABELS: Record<TabId, string> = { regions: "Regions", categories: "Categories", sources: "Sources" };

/* Shared looks. Heights are in px so the phone text-size setting (up to 1.7x) enlarges
   the words without turning every button into a tall block. */
const pillClass = (active: boolean) =>
  cn(
    "inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border px-[14px] text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-1 sm:min-h-[38px] sm:text-sm",
    active
      ? "border-[#1D9E75] bg-[#e5f3ec] text-[#105b45]"
      : "border-[#d3cabd] bg-[#fffef9] text-[#3e3a33] hover:border-[#8fbb9f] hover:bg-[#f6faf6]",
  );
const sectionClass =
  "rounded-[16px] border border-[#e3ddd3] bg-white p-[12px] shadow-[0_1px_2px_rgba(41,39,33,0.05)] sm:p-4";
const sectionHeadingClass = "flex items-center gap-2 text-base font-semibold text-[#292721]";
const groupLabelClass = "text-xs font-semibold uppercase tracking-wide text-[#746d62]";
const textLinkClass =
  "min-h-[40px] shrink-0 px-1 text-[length:min(0.875rem,4.4vw)] font-semibold text-[#17664f] underline decoration-[#9fcfb8] underline-offset-4 outline-none hover:text-[#0f4f3c] focus-visible:ring-2 focus-visible:ring-[#1D9E75] sm:text-sm";

export default function FiltersDialog({
  open,
  categories,
  sources,
  lockedRegions = [],
  initialDraft,
  onApply,
  onCancel,
}: FiltersDialogProps) {
  const [draft, setDraft] = useState<DialogFilterDraft>(initialDraft);
  const [activeTab, setActiveTab] = useState<TabId>("regions");
  const [sourceSearch, setSourceSearch] = useState("");
  const tabRefs = useRef<Record<TabId, HTMLButtonElement | null>>({ regions: null, categories: null, sources: null });
  const sourceRowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  // Phones get regions and categories only; sources is a long list best left to desktop
  const isMobile = useIsMobile();
  const tabs: TabId[] = isMobile ? ["regions", "categories"] : ["regions", "categories", "sources"];
  const shownTab = tabs.includes(activeTab) ? activeTab : "regions";

  const { data: regionsData } = useRegions({});

  // Total (unfiltered) counts — used to hide regions and categories with no upcoming events
  const { data: totalCounts } = useUnifiedFilterCounts({});

  const allRegionNames = useMemo(
    () => regionsData?.regions.map(({ region }) => region) ?? [],
    [regionsData],
  );
  const sortedSources = useMemo(() => [...sources].sort((a, b) => a.localeCompare(b)), [sources]);
  const lockedRegionsKey = lockedRegions.join("\u0000");

  // Snapshot initialDraft each time the dialog opens
  useEffect(() => {
    if (!open) return;
    setDraft(initialDraft);
    setSourceSearch("");
    setActiveTab("regions");
  }, [initialDraft, open]);

  // Old links may carry hide-style (excluded) params; show them as the equivalent selection.
  // Uses the full, unfiltered option lists so no hidden-but-valid option is dropped.
  useEffect(() => {
    if (!open) return;
    setDraft((d) =>
      normalizeDraftToSelection(d, { regions: allRegionNames, categories, sources: sortedSources }, lockedRegions),
    );
    // lockedRegions is a fresh array each render; lockedRegionsKey tracks its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDraft, allRegionNames, categories, sortedSources, lockedRegionsKey]);

  // Hide options with no upcoming events at all, but never hide something that is selected.
  const keepOption = (facet: "regions" | "categories", value: string) => {
    if (!totalCounts) return true;
    const totals = facet === "regions" ? totalCounts.regionCounts : totalCounts.categoryCounts;
    return (
      (totals[value] ?? 0) > 0 ||
      draft[SELECTED_KEY[facet]].includes(value) ||
      (facet === "regions" && lockedRegions.includes(value))
    );
  };
  const regionNames = allRegionNames.filter((r) => keepOption("regions", r));
  const categoryNames = categories.filter((c) => keepOption("categories", c));

  // Draft-aware counts — mirrors the dates the applied query would use
  const { data: counts, isFetching } = useUnifiedFilterCounts({
    dates: derivedDatesForFilter(draft.dateFilter, draft.customDate),
    sources: draft.selectedSources.length ? draft.selectedSources : undefined,
    excludedSources: draft.excludedSources.length ? draft.excludedSources : undefined,
    categories: draft.selectedCategories.length ? draft.selectedCategories : undefined,
    excludedCategories: draft.excludedCategories.length ? draft.excludedCategories : undefined,
    timePeriods: draft.timePeriods.includes("allDay") ? undefined : draft.timePeriods,
    regions: draft.selectedRegions.length ? draft.selectedRegions : undefined,
    excludedRegions: draft.excludedRegions.length ? draft.excludedRegions : undefined,
  });

  const facetValues = { regions: regionNames, categories: categoryNames, sources: sortedSources };
  // The options the visitor has picked. Empty means "all" for that facet.
  const facetSelected: Record<TabId, string[]> = {
    regions: [...new Set([...lockedRegions, ...draft.selectedRegions])],
    categories: draft.selectedCategories,
    sources: draft.selectedSources,
  };
  // Picks the visitor can remove with Clear (route-locked regions are not theirs to clear)
  const clearableCount = (facet: TabId) =>
    facetSelected[facet].filter((v) => !(facet === "regions" && lockedRegions.includes(v))).length;
  const visibleSources = sourceSearch.trim()
    ? sortedSources.filter((s) => s.toLowerCase().includes(sourceSearch.trim().toLowerCase()))
    : sortedSources;
  const sourceLetters = new Set(
    sortedSources.map((s) => s[0]?.toUpperCase()).filter((l) => /^[A-Z]$/.test(l ?? "")),
  );
  const resultLabel = formatDraftResultCount(counts?.totalEvents, isFetching);
  const noMatches = counts?.totalEvents === 0 && !isFetching;
  const activeFilters = describeActiveFilters(draft, lockedRegions);
  const removeFilter = (key: ActiveFilterKey) => {
    if (key === "date") setDraft((d) => ({ ...d, dateFilter: null, customDate: null }));
    else if (key === "time") setDraft((d) => ({ ...d, timePeriods: ["allDay"] }));
    else setDraft((d) => clearFacetSelection(d, key, lockedRegions));
  };

  const onTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
    e.preventDefault();
    const next =
      e.key === "Home" ? 0
      : e.key === "End" ? tabs.length - 1
      : (index + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
    setActiveTab(tabs[next]);
    tabRefs.current[tabs[next]]?.focus();
  };

  /* ── Date / time pill data ── */
  const datePills: { label: string; value: NonNullable<DateFilterType> }[] = [
    { label: "Today", value: "today" },
    { label: "Tomorrow", value: "tomorrow" },
    { label: "This weekend", value: "weekend" },
  ];
  const timePills: { label: string; value: TimePeriod }[] = [
    { label: "Any time", value: "allDay" },
    { label: "Morning", value: "morning" },
    { label: "Afternoon", value: "afternoon" },
    { label: "Evening", value: "evening" },
  ];
  const hasDateOrTime = draft.dateFilter != null || !draft.timePeriods.includes("allDay");

  /* ── Row renderer ── */
  const renderRows = (facet: TabId) => {
    const values = facet === "sources" ? visibleSources : facetValues[facet];
    const selected = new Set(facetSelected[facet]);
    const countMap =
      facet === "regions" ? counts?.regionCounts
      : facet === "categories" ? counts?.categoryCounts
      : counts?.sourceCounts;

    if (!values.length) {
      return (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {facet === "sources" && sourceSearch ? `No sources match "${sourceSearch}".` : "No options available."}
        </p>
      );
    }
    return values.map((value) => {
      const included = selected.has(value);
      const locked = facet === "regions" && lockedRegions.includes(value);
      // Options with no matches are missing from the count map once counts have loaded
      const count = countMap ? (countMap[value] ?? 0) : undefined;
      const countLabel = count == null ? "…" : `${count} ${count === 1 ? "event" : "events"}`;
      const empty = count === 0 && !included;
      return (
        <button
          key={value}
          ref={
            facet === "sources"
              ? (node) => { if (node) sourceRowRefs.current.set(value, node); }
              : undefined
          }
          type="button"
          role="checkbox"
          aria-checked={included}
          aria-label={`${value}${locked ? ", always included on this page" : ""}${count == null ? "" : `, ${countLabel}`}`}
          disabled={locked}
          onClick={() => setDraft((d) => applyFacetToggle(d, facet, value, lockedRegions))}
          className={cn(
            "flex min-h-[48px] w-full items-center gap-3 border-b border-[#f0ece5] px-3 py-2 text-left outline-none transition-colors last:border-b-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1D9E75] disabled:cursor-default",
            included ? "bg-[#f1faf6] hover:bg-[#e6f5ee]" : "bg-white hover:bg-[#f8f6f2]",
          )}
          data-testid={`filter-${facet.slice(0, -1)}-${value.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <span
            className={cn(
              "inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[6px] border-2 transition-colors",
              included ? "border-[#16795e] bg-[#16795e] text-white" : "border-[#c9c1b4] bg-white",
            )}
            aria-hidden
          >
            {included && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
          </span>
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-sm",
              included ? "font-semibold text-[#105b45]" : "font-medium text-[#292721]",
              empty && "text-muted-foreground",
            )}
          >
            {value}
          </span>
          <span className="shrink-0 text-xs text-[#746d62]">
            {locked ? "Always included" : countLabel}
          </span>
        </button>
      );
    });
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[80] bg-[#12231b]/45 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          aria-describedby="filter-dialog-description"
          className="fixed inset-x-0 bottom-0 z-[81] flex h-[calc(100dvh-env(safe-area-inset-top))] max-h-[960px] flex-col overflow-hidden border border-[#d9e2da] bg-[#f5f8f3] shadow-2xl outline-none sm:inset-x-auto sm:left-1/2 sm:top-1/2 sm:h-[min(92dvh,920px)] sm:w-[min(960px,calc(100vw-48px))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[20px]"
          data-testid="filters-dialog"
        >
          {/* ══ Header: title, close, and what the current choices add up to ══ */}
          <header className="shrink-0 border-b border-[#e3ddd3] bg-white px-[16px] pb-3 pt-[calc(12px+env(safe-area-inset-top))] sm:px-6 sm:pt-5">
            <div className="mx-auto max-w-[760px]">
              <div className="flex items-center justify-between gap-4">
                <DialogPrimitive.Title className="text-xl font-bold text-[#292721]">Filters</DialogPrimitive.Title>
                <DialogPrimitive.Description id="filter-dialog-description" className="sr-only">
                  Adjust choices then apply to see matching events.
                </DialogPrimitive.Description>
                <button
                  type="button"
                  onClick={onCancel}
                  className="inline-flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full bg-[#f1ede6] text-[#3e3a33] outline-none transition-colors hover:bg-[#e8e2d8] focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
                  aria-label="Cancel and close filters"
                  title="Close"
                  data-testid="button-close-filters"
                >
                  <X className="h-5 w-5" aria-hidden />
                </button>
              </div>
              <p
                className={cn("mt-0.5 text-sm font-semibold", noMatches ? "text-[#b42318]" : "text-[#16795e]")}
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-testid="filter-result-status"
              >
                {resultLabel}
              </p>
              {activeFilters.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Your selections">
                  {activeFilters.map(({ key, label }) => (
                    <li key={key} className="min-w-0 max-w-full">
                      <button
                        type="button"
                        onClick={() => removeFilter(key)}
                        className="inline-flex min-h-[34px] max-w-full items-center gap-1 rounded-full border border-[#9fcfb8] bg-[#e5f3ec] pl-3 pr-2 text-xs font-semibold text-[#105b45] outline-none transition-colors hover:bg-[#d7ede1] focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
                        aria-label={`Remove filter: ${label}`}
                        data-testid={`active-filter-${key}`}
                      >
                        <span className="truncate">{label}</span>
                        <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </header>

          {/* ══ Scrollable body ══ */}
          <main className="min-h-0 flex-1 overflow-y-auto px-[14px] pb-4 sm:px-6">
            <div className="mx-auto max-w-[760px] space-y-3 pt-3">
              {/* ── When: day and time of day ── */}
              <section aria-labelledby="date-time-heading" className={sectionClass}>
                <div className="flex items-center justify-between gap-2">
                  <h2 id="date-time-heading" className={sectionHeadingClass}>
                    <Calendar className="h-4 w-4 text-[#16795e]" aria-hidden />
                    When
                  </h2>
                  {hasDateOrTime && (
                    <button
                      type="button"
                      onClick={() => setDraft((d) => ({ ...d, dateFilter: null, customDate: null, timePeriods: ["allDay"] }))}
                      className={textLinkClass}
                      aria-label="Clear day and time of day"
                      data-testid="clear-date-time"
                    >
                      Clear
                    </button>
                  )}
                </div>

                <p id="filter-day-label" className={cn(groupLabelClass, "mt-2")}>Day</p>
                <div className="mt-1.5 flex flex-wrap gap-2" role="group" aria-labelledby="filter-day-label">
                  <button
                    type="button"
                    aria-pressed={draft.dateFilter == null}
                    onClick={() => setDraft((d) => ({ ...d, dateFilter: null, customDate: null }))}
                    className={pillClass(draft.dateFilter == null)}
                    data-testid="date-pill-any"
                  >
                    {draft.dateFilter == null && <Check className="h-3.5 w-3.5" aria-hidden />}
                    Any day
                  </button>
                  {datePills.map(({ label, value }) => {
                    const active = draft.dateFilter === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setDraft((d) => applyDateFilter(d, value))}
                        className={pillClass(active)}
                        data-testid={`date-pill-${value}`}
                      >
                        {active && <Check className="h-3.5 w-3.5" aria-hidden />}
                        {label}
                      </button>
                    );
                  })}
                  {draft.dateFilter === "custom" ? (
                    <label className="inline-flex shrink-0 items-center">
                      <span className="sr-only">Pick a specific date</span>
                      <input
                        type="date"
                        value={draft.customDate ?? ""}
                        onChange={(e) =>
                          setDraft((d) => applyDateFilter(d, "custom", e.target.value || undefined))
                        }
                        className="min-h-[44px] rounded-full border border-[#1D9E75] bg-[#e5f3ec] px-[14px] text-xs font-medium text-[#105b45] outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] sm:min-h-[38px] sm:text-sm"
                        data-testid="custom-date-input"
                      />
                    </label>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setDraft((d) => applyDateFilter(d, "custom"))}
                      className={pillClass(false)}
                      data-testid="date-pill-custom"
                    >
                      <Calendar className="h-3.5 w-3.5 text-[#16795e]" aria-hidden />
                      Pick a date
                    </button>
                  )}
                </div>

                <p id="filter-time-label" className={cn(groupLabelClass, "mt-3")}>Time of day</p>
                <div className="mt-1.5 flex flex-wrap gap-2" role="group" aria-labelledby="filter-time-label">
                  {timePills.map(({ label, value }) => {
                    const active = draft.timePeriods.includes(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setDraft((d) => applyTimePeriodToggle(d, value))}
                        className={pillClass(active)}
                        data-testid={`time-pill-${value}`}
                      >
                        {active && <Check className="h-3.5 w-3.5" aria-hidden />}
                        {label}
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* ── Where & what: regions, categories (and sources on desktop) ── */}
              <section aria-labelledby="facet-heading" className={sectionClass}>
                <h2 id="facet-heading" className={sectionHeadingClass}>
                  <MapPin className="h-4 w-4 text-[#16795e]" aria-hidden />
                  Where &amp; what
                </h2>

                {/* Switcher stays pinned while the options scroll */}
                <div className="sticky top-0 z-10 -mx-[12px] bg-white px-[12px] pb-2 pt-2.5 sm:-mx-4 sm:px-4">
                  <div
                    className="grid gap-[4px] rounded-full bg-[#eef3ef] p-[4px]"
                    style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
                    role="tablist"
                    aria-label="Filter categories"
                  >
                    {tabs.map((tab, index) => {
                      const picked = clearableCount(tab);
                      const active = shownTab === tab;
                      return (
                        <button
                          key={tab}
                          ref={(node) => { tabRefs.current[tab] = node; }}
                          type="button"
                          role="tab"
                          id={`filter-tab-${tab}`}
                          aria-controls={`filter-panel-${tab}`}
                          aria-selected={active}
                          aria-label={`${TAB_LABELS[tab]}: ${formatFacetSelectionSummary(tab, facetSelected[tab])}`}
                          tabIndex={active ? 0 : -1}
                          onClick={() => setActiveTab(tab)}
                          onKeyDown={(e) => onTabKeyDown(e, index)}
                          className={cn(
                            "flex min-h-[40px] min-w-0 items-center justify-center gap-[6px] rounded-full px-[8px] text-[length:min(0.875rem,4.4vw)] font-semibold outline-none sm:text-sm transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1D9E75]",
                            active
                              ? "bg-white text-[#105b45] shadow-[0_1px_3px_rgba(16,91,69,0.2)]"
                              : "text-[#5d574e] hover:text-[#292721]",
                          )}
                          data-testid={`tab-${tab}`}
                        >
                          <span className="truncate">{TAB_LABELS[tab]}</span>
                          {picked > 0 && (
                            <span
                              className="inline-flex h-[20px] min-w-[20px] shrink-0 items-center justify-center rounded-full bg-[#16795e] px-1 text-[11px] font-bold leading-none text-white"
                              aria-hidden
                            >
                              {picked}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div
                  id={`filter-panel-${shownTab}`}
                  role="tabpanel"
                  aria-labelledby={`filter-tab-${shownTab}`}
                  className="space-y-2 pt-1"
                >
                  {/* Sources: search + A–Z jump */}
                  {shownTab === "sources" && (
                    <div className="space-y-2">
                      <label className="relative block">
                        <span className="sr-only">Search sources</span>
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                        <input
                          value={sourceSearch}
                          onChange={(e) => setSourceSearch(e.target.value)}
                          className="min-h-[44px] w-full rounded-full border border-[#d3cabd] bg-[#fffef9] py-2 pl-10 pr-4 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
                          placeholder="Search sources…"
                          data-testid="search-sources"
                        />
                      </label>
                      <p className="text-xs text-muted-foreground" aria-live="polite">
                        {sourceSearch
                          ? `${visibleSources.length} of ${sortedSources.length} sources match.`
                          : `${sortedSources.length} sources available.`}
                      </p>
                      <div
                        className="grid grid-cols-[repeat(13,minmax(0,1fr))] gap-0.5 sm:grid-cols-[repeat(26,minmax(0,1fr))]"
                        aria-label="Jump to source by first letter"
                      >
                        {letters.map((letter) => {
                          const available = sourceLetters.has(letter);
                          return (
                            <button
                              key={letter}
                              type="button"
                              disabled={!available}
                              onClick={() => {
                                setSourceSearch("");
                                requestAnimationFrame(() =>
                                  sourceRowRefs.current
                                    .get(sortedSources.find((s) => s[0]?.toUpperCase() === letter) ?? "")
                                    ?.scrollIntoView({ block: "center" }),
                                );
                              }}
                              className="min-h-8 rounded-md text-xs font-medium text-foreground outline-none hover:bg-[#f1ede6] focus-visible:ring-2 focus-visible:ring-[#1D9E75] disabled:text-muted-foreground/35 disabled:hover:bg-transparent"
                              aria-label={
                                available
                                  ? `Jump to sources beginning with ${letter}`
                                  : `No sources beginning with ${letter}`
                              }
                            >
                              {letter}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* What this tab's picks add up to */}
                  <div className="flex min-h-[40px] items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[#292721]" data-testid={`selection-summary-${shownTab}`}>
                        {formatFacetSelectionSummary(shownTab, facetSelected[shownTab])}
                      </p>
                      {clearableCount(shownTab) === 0 && (
                        <p className="text-xs text-[#746d62]">
                          Tap one or more to show only those {FACET_NOUNS[shownTab].many}.
                        </p>
                      )}
                    </div>
                    {clearableCount(shownTab) > 0 && (
                      <button
                        type="button"
                        onClick={() => setDraft((d) => clearFacetSelection(d, shownTab, lockedRegions))}
                        className={textLinkClass}
                        aria-label={`Clear ${FACET_NOUNS[shownTab].one} selection and show all ${FACET_NOUNS[shownTab].many}`}
                        data-testid={`clear-selection-${shownTab}`}
                      >
                        Clear
                      </button>
                    )}
                  </div>

                  {/* Facet rows */}
                  <div className="overflow-hidden rounded-[12px] border border-[#ebe6de] bg-white">
                    {renderRows(shownTab)}
                  </div>
                </div>
              </section>
            </div>
          </main>

          {/* ══ Footer ══ */}
          <footer className="shrink-0 border-t border-[#e3ddd3] bg-white px-[16px] pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] sm:px-6">
            <div className="mx-auto flex max-w-[760px] items-center gap-[14px]">
              <button
                type="button"
                onClick={() => setDraft(emptyFilterDraft(lockedRegions))}
                className={textLinkClass}
                data-testid="reset-all-filters"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={() => {
                  trackEvent("filters_applied", {
                    has_date_filter: draft.dateFilter !== null,
                    has_time_filter: !draft.timePeriods.includes("allDay"),
                    has_category_filter:
                      draft.selectedCategories.length > 0 ||
                      draft.excludedCategories.length > 0,
                    has_source_filter:
                      draft.selectedSources.length > 0 ||
                      draft.excludedSources.length > 0,
                    has_region_filter:
                      draft.selectedRegions.length > 0 ||
                      draft.excludedRegions.length > 0,
                    result_count: counts?.totalEvents ?? -1,
                  });
                  onApply(draft);
                }}
                className="ml-auto inline-flex min-h-[48px] min-w-0 flex-1 items-center justify-center whitespace-nowrap rounded-full bg-[#16795e] px-[18px] text-[length:min(0.875rem,4.6vw)] font-semibold text-white shadow-[0_6px_14px_-4px_rgba(22,121,94,0.45)] outline-none transition-colors hover:bg-[#0f5f49] focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 sm:flex-none sm:px-8 sm:text-sm"
                data-testid="apply-filters"
              >
                Show{" "}
                {counts?.totalEvents == null
                  ? "events"
                  : `${counts.totalEvents.toLocaleString()} ${counts.totalEvents === 1 ? "event" : "events"}`}
              </button>
            </div>
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
